import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { euro } from "@/lib/api";
import { SectionTitle, TempBadge } from "@/components/common";
import { toast } from "sonner";
import { Lock, GripVertical, ArrowRight } from "lucide-react";

const COLUMNS = [
  { key: "nuova", label: "Notizia", color: "#8A8A94" },
  { key: "da_contattare", label: "Primo Contatto", color: "#38BDF8" },
  { key: "follow_up", label: "Follow-up", color: "#FB923C" },
  { key: "appuntamento", label: "Appuntamento", color: "#A78BFA" },
  { key: "valutazione", label: "Valutazione", color: "#FFB020" },
  { key: "trattativa", label: "Trattativa", color: "#FC82ED" },
  { key: "incarico_acquisito", label: "Incarico", color: "#34D399" },
];
// non-owned notizie states shown as-is in matching column if present
const COL_KEYS = COLUMNS.map((c) => c.key);

export default function Pipeline() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = () => api.get("/notizie").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const byCol = (key) => items.filter((n) => (COL_KEYS.includes(n.stato) ? n.stato : "nuova") === key);

  const onDrop = async (colKey) => {
    setOverCol(null);
    const n = items.find((x) => x.id === dragId);
    setDragId(null);
    if (!n || n.stato === colKey || n._masked) return;
    const prev = items;
    setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, stato: colKey } : x)));
    try {
      await api.put(`/notizie/${n.id}`, { stato: colKey });
      toast.success(`Spostata in "${COLUMNS.find((c) => c.key === colKey)?.label}"`);
    } catch (e) { setItems(prev); toast.error("Impossibile spostare questa notizia"); }
  };

  return (
    <div className="fade-up">
      <SectionTitle sub="Trascina le notizie tra le fasi: Notizia → Primo Contatto → … → Incarico. Il metodo guida ogni passaggio.">Pipeline Acquisizione</SectionTitle>

      {loading ? <div className="skeleton h-96 rounded-2xl" /> :
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const cards = byCol(col.key);
          return (
            <div key={col.key} data-testid={`col-${col.key}`}
                 onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
                 onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
                 onDrop={() => onDrop(col.key)}
                 className={`w-[280px] shrink-0 rounded-2xl border transition-colors ${overCol === col.key ? "border-[#FC82ED] bg-[#FC82ED]/[0.04]" : "border-[var(--border)] bg-[#F7F7F8]"}`}>
              <div className="p-3 flex items-center justify-between sticky top-0">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                  <span className="font-display font-bold text-sm">{col.label}</span>
                </div>
                <span className="mono text-xs text-[var(--muted)] bg-slate-100 px-2 py-0.5 rounded-full">{cards.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[120px]">
                {cards.map((n) => (
                  <div key={n.id} data-testid={`pipeline-card-${n.id}`}
                       draggable={!n._masked}
                       onDragStart={() => setDragId(n.id)}
                       onDragEnd={() => { setDragId(null); setOverCol(null); }}
                       className={`card-immo p-3 ${n._masked ? "opacity-70" : "cursor-grab active:cursor-grabbing hover:border-[#FC82ED]/40"} ${dragId === n.id ? "ring-1 ring-[#FC82ED]" : ""}`}>
                    <div className="flex items-start gap-2">
                      {!n._masked && <GripVertical size={14} className="text-[var(--muted)] mt-0.5 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{n._masked ? (n.nome_display || "Riservato") : `${n.nome} ${n.cognome}`}</div>
                        <div className="text-xs text-[var(--muted)] truncate">{n.zona} · {n.tipologia}</div>
                      </div>
                      <TempBadge value={n.temperatura} />
                    </div>
                    {n.valore_stimato > 0 && <div className="mono font-bold text-[#FC82ED] text-sm mt-2">{euro(n.valore_stimato)}</div>}
                    {n._masked ? (
                      <div className="mt-2 text-[11px] text-[var(--muted)] flex items-center gap-1"><Lock size={11}/> {n.agente_referente}</div>
                    ) : (
                      col.key === "incarico_acquisito" && (
                        <button data-testid={`goto-immobile-${n.id}`} onClick={() => n.immobile_id && nav(`/immobili/${n.immobile_id}`)}
                                className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-[#34D399]">Vai all'immobile <ArrowRight size={12}/></button>
                      )
                    )}
                  </div>
                ))}
                {cards.length === 0 && <div className="text-center text-[11px] text-[var(--muted)] py-6">Trascina qui</div>}
              </div>
            </div>
          );
        })}
      </div>}
    </div>
  );
}
